/**
 * SandboxDiagnosticsPanel — the Diagnostics tab of the sandbox sidebar.
 *
 * Flat editorial sections (Health checks / Provision timing / Host metrics) to
 * match the rest of the sandbox surfaces. Provision timing is always available
 * (historical); live host metrics are the only section gated on a running host.
 *
 * @module features/sandboxes/components/SandboxDiagnosticsPanel
 */
import React from 'react';
import { SettingsSection, Sparkline, Spinner, Text, cn } from '@chatsdet/ui';
import {
  useInstanceMetrics,
  type EnvironmentInstance,
  type HealthCheckResult,
  type InstanceMetricSeries,
} from '../../../hooks/useEnvironmentInstances';
import { useProvisionTimings } from '../hooks/useProvisionTimings';
import { ProvisionTimingCard } from './overview/ProvisionTimingCard';
import { getSandboxFailureDiagnostics } from './sandboxFailure';
import { formatRelative } from './overview/time-format';

const BLUE = '#4DA6FF';
const ORANGE = '#FFBD0F';

type MetricView = {
  key: string;
  label: string;
  format: (v: number) => string;
};

const METRIC_VIEWS: MetricView[] = [
  { key: 'cpu', label: 'CPU', format: (v) => `${v.toFixed(1)}%` },
  { key: 'networkIn', label: 'Network in', format: formatBytesPerMin },
  { key: 'networkOut', label: 'Network out', format: formatBytesPerMin },
  { key: 'ebsReadBytes', label: 'EBS read', format: formatBytesPerMin },
  { key: 'ebsWriteBytes', label: 'EBS write', format: formatBytesPerMin },
  { key: 'statusCheckFailed', label: 'Status checks', format: (v) => (v > 0 ? 'Failing' : 'OK') },
];

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Network/EBS EC2 metrics are totals per period; show a per-minute rate. */
function formatBytesPerMin(v: number): string {
  return `${formatBytes(v)}/min`;
}

function latestValue(series: InstanceMetricSeries | undefined): number | null {
  if (!series || series.datapoints.length === 0) return null;
  return series.datapoints[series.datapoints.length - 1]!.v;
}

/**
 * Split a formatted metric into its numeric part and trailing unit so the value
 * reads as a prominent foreground number with a quieter unit (e.g. **35.6**
 * ·`KB/min`). Non-numeric values ("Failing", "OK", "—") return no unit.
 */
export function splitValueUnit(formatted: string): { value: string; unit: string } {
  const match = formatted.match(/^(-?[\d.,]+)\s*(.*)$/);
  if (!match) return { value: formatted, unit: '' };
  return { value: match[1]!, unit: match[2]! };
}

/** One flat metric row: label · sparkline · latest value. */
const MetricRow: React.FC<{ view: MetricView; series?: InstanceMetricSeries }> = ({
  view,
  series,
}) => {
  const points = (series?.datapoints ?? []).map((d) => d.v);
  const latest = latestValue(series);
  const isStatusBreaching = view.key === 'statusCheckFailed' && (latest ?? 0) > 0;
  const isCpuHot = view.key === 'cpu' && (latest ?? 0) >= 80;
  const isAlert = isStatusBreaching || isCpuHot;
  // Sparkline keeps the saturated accent; the value text stays native foreground
  // (amber only when a metric is actually breaching).
  const sparkColor = isAlert ? ORANGE : BLUE;
  const { value, unit } = splitValueUnit(latest === null ? '—' : view.format(latest));

  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <Text size="sm" weight="medium">
        {view.label}
      </Text>
      <div className="flex items-center gap-3">
        {points.length > 0 && (
          <Sparkline
            points={points}
            color={sparkColor}
            width={72}
            height={22}
            aria-label={`${view.label} trend`}
          />
        )}
        <span
          className={cn(
            'text-sm font-semibold tabular-nums w-20 text-right',
            isAlert ? 'text-amber-600' : 'text-foreground'
          )}
        >
          {value}
          {unit && <span className="ml-0.5 text-xs font-normal text-muted-foreground">{unit}</span>}
        </span>
      </div>
    </div>
  );
};

const HealthRow: React.FC<{ result: HealthCheckResult }> = ({ result }) => (
  <div className="flex items-center gap-3 py-2.5">
    <span
      aria-hidden
      className={`h-2 w-2 rounded-full shrink-0 ${result.passed ? 'bg-emerald-500' : 'bg-red-500'}`}
    />
    <span className="text-sm font-medium flex-1 truncate">{result.name}</span>
    <span className="text-xs font-mono text-muted-foreground truncate max-w-[150px]">
      {result.details}
    </span>
    <span
      className={`text-xs font-medium ${result.passed ? 'text-emerald-700' : 'text-red-700'}`}
    >
      {result.passed ? 'passing' : 'failing'}
    </span>
  </div>
);

export interface SandboxDiagnosticsPanelProps {
  instance: EnvironmentInstance;
  /** Whether the instance currently has a bound, running EC2 host. */
  runtimeAvailable: boolean;
}

export function SandboxDiagnosticsPanel({
  instance,
  runtimeAvailable,
}: SandboxDiagnosticsPanelProps) {
  const instanceId = instance.id;
  const healthResults = instance.healthCheckResults ?? [];
  const { data: provisionTimings } = useProvisionTimings(instanceId);
  // Superadmins get per-step rows; everyone else gets the ready/build summary.
  const hasProvisionTiming =
    (provisionTimings?.timings?.length ?? 0) > 0 || Boolean(provisionTimings?.summary);
  const { data, isLoading, isError } = useInstanceMetrics(instanceId, {
    enabled: runtimeAvailable,
  });
  const metrics = data?.data ?? null;
  const seriesByKey = new Map((metrics?.series ?? []).map((s) => [s.key, s]));

  return (
    <div className="h-full overflow-y-auto px-4 py-5">
      {healthResults.length > 0 && (
        <SettingsSection
          title="Health checks"
          description={
            instance.lastHealthCheck
              ? `Last checked ${formatRelative(instance.lastHealthCheck)}`
              : undefined
          }
        >
          <div className="divide-y divide-border -mt-1">
            {healthResults.map((r) => (
              <HealthRow key={r.name} result={r} />
            ))}
          </div>
        </SettingsSection>
      )}

      <SettingsSection title="Provision timing">
        {hasProvisionTiming ? (
          <ProvisionTimingCard
            timings={provisionTimings?.timings ?? null}
            summary={provisionTimings?.summary ?? null}
            failedStep={
              instance.status === 'failed'
                ? getSandboxFailureDiagnostics(healthResults).step
                : null
            }
          />
        ) : (
          <Text size="sm" color="muted">
            No provision timing recorded for this instance.
          </Text>
        )}
      </SettingsSection>

      <SettingsSection
        title="Host metrics"
        description={metrics ? `Last ${metrics.windowMinutes} min · AWS/EC2` : undefined}
      >
        {!runtimeAvailable ? (
          <Text size="sm" color="muted">
            Live host metrics are available while the sandbox is running.
          </Text>
        ) : isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Spinner /> <Text size="sm">Loading host metrics…</Text>
          </div>
        ) : isError ? (
          <Text size="sm" color="muted">
            Couldn&apos;t load host metrics. They may be briefly unavailable — this view refreshes
            automatically.
          </Text>
        ) : !metrics ? (
          <Text size="sm" color="muted">
            No host metrics yet. CloudWatch publishes EC2 metrics a few minutes after the instance
            starts.
          </Text>
        ) : (
          <div className="divide-y divide-border -mt-1">
            {METRIC_VIEWS.map((view) => (
              <MetricRow key={view.key} view={view} series={seriesByKey.get(view.key)} />
            ))}
          </div>
        )}
      </SettingsSection>
    </div>
  );
}

SandboxDiagnosticsPanel.displayName = 'SandboxDiagnosticsPanel';
